import 'vanilla-cookieconsent/dist/cookieconsent.css';

import * as CookieConsent from "vanilla-cookieconsent";

import { createConsentServices } from './ConsentServices';

const revision = 8

const cc = CookieConsent

window.cc = cc

const services = createConsentServices({
    consent: cc,
    window,
    document,
    adClient: document.querySelector('meta[name="adsense-client"]')?.getAttribute('content'),
    loadSentry: () => import('@sentry/browser'),
    sentryOptions: {
        dsn: import.meta.env.VITE_SENTRY_DSN,
        release: window.appVersion,
        sendDefaultPii: true,
        tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0,
        tracePropagationTargets: ['localhost', /^https:\/\/sbahn-meldungen\.de\/api/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    },
})

cc.run({
    mode: 'opt-in',
    autoShow: true,
    revision,
    guiOptions: {
        consentModal: {
            layout: 'box wide',
            position: 'middle center',
        }
    },

    disablePageInteraction: true,

    onFirstConsent: () => {
        logConsent()
    },

    onChange: () => {
        logConsent()

        if (services.requiresReload()) {
            window.location.reload()
        }
    },

    categories: {
        necessary: {
            enabled: true,  // this category is enabled by default
            readOnly: true  // this category cannot be disabled
        },
        analytics: {
            enabled: false,
            readOnly: false,
            services: {
                matomo: {
                    label: 'Matomo',
                    onReject: services.rejectService,
                    cookies: [
                        { name: /^_pk_/ },
                        { name: 'mtm_consent' },
                        { name: 'mtm_cookie_consent' },
                    ],
                },
                sentry_io: {
                    label: 'Sentry.io',
                    onAccept: services.enableSentry,
                    onReject: services.rejectService,
                    cookies: [{ name: /^sentryReplaySession/ }],
                },
            },
        },
        marketing: {
            enabled: false,
            readOnly: false,
            services: {
                adsbygoogle: {
                    label: 'Google AdSense und Google-Einwilligungsdialog laden',
                    onAccept: services.enableAds,
                    onReject: services.rejectAds,
                    cookies: [{ name: /^__gads$|^__gpi$|^__eoi$|^_gcl_/ }],
                },
            },
        },
    },
    language: {
        default: 'de',

        translations: {
            de: {
                consentModal: {
                    title: 'Cookies und externe Dienste',
                    description: 'Mit „Alle akzeptieren“ erlaubst du Matomo zur Reichweitenmessung, Sentry zur Fehleranalyse und das Laden von Google AdSense samt Google-Einwilligungsdialog. Beim Laden der Google-Dienste werden deine IP-Adresse und Browserdaten an Google übertragen. Über die weitere Werbeverarbeitung entscheidest du anschließend bei Google, sofern dort noch keine Auswahl gespeichert ist. Du kannst alle optionalen Dienste ablehnen oder einzeln auswählen.',
                    acceptAllBtn: 'Alle akzeptieren',
                    acceptNecessaryBtn: 'Alle ablehnen',
                    showPreferencesBtn: 'Individuelle Konfiguration'
                },
                preferencesModal: {
                    title: 'Datenschutz-Einstellungen',
                    acceptAllBtn: 'Alle akzeptieren',
                    acceptNecessaryBtn: 'Alle ablehnen',
                    savePreferencesBtn: 'Aktuelle Einstellungen speichern',
                    closeIconLabel: 'Datenschutz-Einstellungen schließen',
                    sections: [
                        {
                            title: 'Optionale Dienste auswählen',
                            description: 'Matomo, Sentry und das Laden der Google-Dienste kannst du unabhängig voneinander erlauben oder ablehnen. Ohne deine Zustimmung bleiben die jeweiligen Dienste blockiert. Deine Auswahl kannst du jederzeit über die Datenschutz-Einstellungen im Footer ändern.'
                        }, {
                            title: 'Erforderliche Cookies',
                            description: 'Diese Dienste sind für die korrekte Funktion dieser Website unerlässlich. Sie können nicht deaktiviert werden, da die Website sonst nicht richtig funktionieren würde.',
                            linkedCategory: 'necessary'
                        }, {
                            title: 'Reichweitenmessung und Fehleranalyse',
                            description: 'Matomo erfasst Besucherstatistiken. Sentry erfasst technische Fehler, Leistungsdaten und gegebenenfalls Sitzungsaufzeichnungen. Dabei können personenbezogene Daten verarbeitet werden. Beide Dienste sind optional und können einzeln aktiviert werden.',
                            linkedCategory: 'analytics'
                        }, {
                            title: 'Google-Dienste laden',
                            description: 'Mit dieser Auswahl erlaubst du das Laden von Google AdSense und Googles Einwilligungsdialog von Google-Servern. Dabei werden bereits deine IP-Adresse und Browserdaten an Google übermittelt; eine Verarbeitung in den USA ist möglich. Die weitere Werbeverarbeitung, insbesondere personalisierte Werbung und Werbepartner, verwaltest du zusätzlich in Googles Einwilligungsdialog. Lehnst du diese Auswahl ab, laden wir weder das AdSense-Skript noch Googles Dialog. Mehr dazu in <a href="https://policies.google.com/privacy">Googles Datenschutzerklärung</a>.',
                            linkedCategory: 'marketing'
                        }, {
                            title: 'Mehr Informationen',
                            description: 'Informationen zur Datenverarbeitung findest du in der <a href="/datenschutzerklaerung">Datenschutzerklärung</a>.'
                        }
                    ]
                }
            }
        }
    }
})

function logConsent() {
    const cookie = cc.getCookie();
    const preferences = cc.getUserPreferences();

    const userConsent = {
        consentId: cookie.consentId,
        acceptType: preferences.acceptType,
        acceptedCategories: preferences.acceptedCategories,
        acceptedServices: preferences.acceptedServices,
        revision,
    };

    fetch('/api/cookie-consent', {
        method: 'POST',
        keepalive: true,
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json, text-plain, */*",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
        },
        body: JSON.stringify(userConsent)
    }).then(response => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`)
        }
    }).catch(error => {
        console.error('Die Cookie-Einwilligung konnte nicht protokolliert werden:', error)
    })
}
